/**
 * Game Configuration - Riley vs Virus
 * Contains all balance values, spawn rates, and game constants
 * Based on the biochemistry-to-gameplay mapping from the design doc
 */

// ============================================================================
// PLAYER CONFIGURATION
// ============================================================================
export const PLAYER = {
    /** Base movement speed in pixels per second */
    MOVE_SPEED: 200,
    /** Maximum health points (Membrane Integrity) */
    MAX_HP: 100,
    /** Collision radius in pixels */
    RADIUS: 20,
    /** Distance threshold to stop moving toward finger (prevents jitter) */
    DEADZONE: 5,
} as const;

// ============================================================================
// VIRUS (ENEMY) CONFIGURATION
// ============================================================================
export const VIRUS = {
    /** Base movement speed (increases over time) */
    BASE_SPEED: 50,
    /** Max speed at Crisis phase */
    MAX_SPEED: 120,
    /** Damage dealt to player on contact */
    DAMAGE: 10,
    /** Collision radius - INCREASED for better visibility */
    RADIUS: 12,
    /** XP value when killed */
    XP_VALUE: 1,
} as const;

export const ENEMIES = {
    RHINOVIRUS: {
        NAME: 'Rhinovirus',
        SPEED: 220, // INCREASED - fast swarmer
        HP: 10, // destroyed by single hit (25 dmg)
        DAMAGE: 5,
        RADIUS: 6,
        MASS: 1, // Light
        XP_VALUE: 1,
        COLOR: '#4ade80', // Green
    },
    ADENOVIRUS: {
        NAME: 'Adenovirus',
        SPEED: 40,
        HP: 80, // 3-4 hits
        DAMAGE: 15,
        RADIUS: 12,
        MASS: 10, // Heavy
        XP_VALUE: 5,
        COLOR: '#a855f7', // Purple
    },
    MUTATED_STRAIN: {
        NAME: 'Mutated Strain',
        SPEED: 100, // Variable/Burst
        HP: 500, // Mini-boss
        DAMAGE: 20,
        RADIUS: 25,
        MASS: 100, // Immovable
        XP_VALUE: 50,
        COLOR: '#3b82f6', // Blue
        SPAWN_COOLDOWN: 5000,
    }
} as const;

export const ITEMS = {
    LIPID_RAFT: {
        NAME: 'Lipid Raft',
        HEAL_PERCENT: 0.05, // Restores 25% max HP
        RADIUS: 15,
        COLOR: '#fbbf24', // Gold
        SPAWN_CHANCE: 0.05, // 5% chance on enemy death? Or just spawning periodically?
    }
} as const;

// ============================================================================
// SPAWNING CONFIGURATION
// ============================================================================
export const SPAWNING = {
    /** Starting spawn interval (ms) */
    INITIAL_INTERVAL: 2000,
    /** Minimum spawn interval at peak difficulty */
    MIN_INTERVAL: 50,
    /** Distance from screen edge to spawn */
    SPAWN_MARGIN: 50,
    /** Max viruses in object pool (reduced for performance) */
    POOL_SIZE: 500,
} as const;

// ============================================================================
// WEAPON CONFIGURATION
// ============================================================================
export const WEAPONS = {
    ANTIBODY: {
        /** Base fire rate cooldown (ms) */
        COOLDOWN: 1000,
        /** Min cooldown after upgrades */
        MIN_COOLDOWN: 40,
        /** Projectile speed */
        SPEED: 400,
        /** Base damage */
        DAMAGE: 25,
        /** Projectile radius */
        RADIUS: 6,
        /** How often to recalculate target (ms) - optimization */
        TARGET_UPDATE_INTERVAL: 100,
    },
    CYTOKINE_STORM: {
        /** AOE radius */
        RADIUS: 150,
        /** Damage dealt */
        DAMAGE: 50,
        /** Cooldown between uses (ms) */
        COOLDOWN: 10000,
    },
} as const;

// ============================================================================
// PROGRESSION CONFIGURATION
// ============================================================================
export const PROGRESSION = {
    /** XP required for first level */
    BASE_XP: 5,
    /** XP multiplier per level */
    XP_SCALE: 1.5,
    /** Magnet (Chemotaxis) pickup radius */
    PICKUP_RADIUS: 30,
    /** Enhanced pickup radius with magnet power-up */
    MAGNET_RADIUS: 150,
} as const;

// ============================================================================
// GAME PHASES (Based on design doc narrative arc)
// ============================================================================
export const PHASES = {
    /** Prodromal phase - slow, exploratory */
    PRODROMAL: {
        startTime: 0,
        endTime: 60000, // 0-1 minute
        spawnInterval: 250,  // Changed from 100 - was causing game freeze
        virusSpeed: 80,
        screenTint: null,
    },
    /** Acute phase - fever kicks in */
    ACUTE: {
        startTime: 60000,
        endTime: 180000, // 1-3 minutes
        spawnInterval: 100,
        virusSpeed: 100,
        screenTint: { r: 180, g: 20, b: 20, a: 0.15 },
    },
    /** Crisis phase - maximum intensity */
    CRISIS: {
        startTime: 180000,
        endTime: 300000, // 3-5 minutes
        spawnInterval: 50,
        virusSpeed: 120,
        screenTint: { r: 180, g: 20, b: 20, a: 0.3 },
    },
} as const;

// ============================================================================
// GAME TIMING
// ============================================================================
export const TIMING = {
    /** Total game duration until victory (ms) - 5 minutes */
    GAME_DURATION: 300000,
    /** Respawn delay after death (ms) */
    RESPAWN_DELAY: 2000,
    /** Timer penalty on death (ms) - makes it harder to win */
    DEATH_PENALTY: 15000,
} as const;

// ============================================================================
// VISUAL CONFIGURATION
// ============================================================================
export const VISUALS = {
    /** Game logical resolution (viewport) */
    WIDTH: 800,
    HEIGHT: 600,
    /** World size (larger than viewport for scrolling) */
    WORLD_WIDTH: 2400,
    WORLD_HEIGHT: 1800,
    /** Contrast Settings - Easily adjust visibility */
    CONTRAST: {
        /** Darken background (0.0 = black, 1.0 = full brightness) */
        BACKGROUND_BRIGHTNESS: 0.3,
        /** Brighten enemies (1.0 = normal, higher = brighter) */
        ENEMY_BRIGHTNESS: 1.2,
        /** Enemy scale multiplier (1.5 = 50% larger than default) */
        ENEMY_SCALE: 1.5,
        /** Camera zoom level (1.0 = normal, 1.5 = 50% zoomed in) */
        CAMERA_ZOOM: 1.3,
    },
    /** Colors */
    COLORS: {
        BACKGROUND: '#222233',
        PLAYER_CYTOPLASM: '#4ade80',
        PLAYER_NUCLEUS: '#14532d',
        PLAYER_MEMBRANE: '#86efac',
        VIRUS_CAPSID: '#ef4444',
        VIRUS_SPIKES: '#b91c1c',
        ANTIBODY: '#60a5fa',
        ANTIGEN_XP: '#fbbf24',
        FEVER_OVERLAY: 'rgba(180, 20, 20, 0.3)',
        CYTOKINE_STORM: '#ff6b35',
    },
} as const;

// ============================================================================
// RESPONSIVE LAYOUT CONFIGURATION
// ============================================================================
export const LAYOUT = {
    /** Maximum width for UI safe zone on desktop (prevents ultrawide spread) */
    MAX_CONTENT_WIDTH: 1200,
    /** Background image dimensions (for center-crop scaling) */
    BACKGROUND_SIZE: 2048,

    // Portrait mode positioning (vertical stack)
    PORTRAIT: {
        /** Title Y position (% from top) */
        TITLE_Y: 0.2,
        /** Character/main visual Y position (% from top) */
        CHARACTER_Y: 0.55,
        /** Buttons Y position (% from top) */
        BUTTONS_Y: 0.75,
        /** Character scale factor */
        CHARACTER_SCALE: 0.8,
    },

    // Landscape mode positioning (side-by-side)
    LANDSCAPE: {
        /** Character X position (% of safe zone from left) */
        CHARACTER_X: 0.25,
        /** Character Y position (% from top) */
        CHARACTER_Y: 0.6,
        /** Title/UI X position (% of safe zone from left) */
        UI_X: 0.7,
        /** Title Y position (% from top) */
        TITLE_Y: 0.3,
        /** Buttons Y position (% from top) */
        BUTTONS_Y: 0.7,
        /** Character base scale factor */
        CHARACTER_SCALE: 0.6,
        /** Max character scale (prevents pixelation on large screens) */
        CHARACTER_MAX_SCALE: 1.0,
    },

    /** Hover scale for buttons (desktop) */
    BUTTON_HOVER_SCALE: 1.1,
} as const;
